import { useCallback, useMemo } from 'react';
import { Option } from 'react-rainbow-components';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import Picklist from 'components/Picklist';
import { Workflow } from '../../../../Skills/types';

const manychatApiValue: PicklistValue = {
    name: 'manychat-api',
    label: 'ManyChat API',
};

const conversationsApiValue: PicklistValue = {
    name: 'conversations-api',
    label: 'Conversations API',
};

export interface ChannelDeliveryPickerValue {
    type: 'workflow' | 'manychat-api' | 'conversations-api';
    id?: string;
}

interface ChannelDeliveryPickerProps {
    // eslint-disable-next-line react/no-unused-prop-types
    channel?: string;
    automations?: Workflow[];
    value?: ChannelDeliveryPickerValue | null | undefined;
    onChange?: (value: ChannelDeliveryPickerValue) => void;
    disabled?: boolean;
    isLoading?: boolean;
}

const ChannelDeliveryPicker = ({
    automations = [],
    value,
    onChange = () => {},
    disabled = false,
    isLoading = false,
}: ChannelDeliveryPickerProps) => {
    const picklistValue = useMemo<PicklistValue>(() => {
        if (value?.type === 'conversations-api') {
            return conversationsApiValue;
        }
        if (!value || value.type !== 'workflow') {
            return manychatApiValue;
        }

        const automation = automations?.find(
            (auto) => auto.id === value.id,
        );

        if (!automation) {
            return {
                name: value.id,
                label: 'Unknown Workflow',
                value: {
                    type: 'workflow',
                    id: value.id,
                },
            };
        }

        return {
            name: automation.id,
            label: automation.name,
            value: {
                type: 'workflow',
                id: automation.id,
            },
        };
    }, [value, automations]);

    const workflowsList = useMemo(
        () => automations?.map(
            (automation) => (
                <Option
                    key={automation.id}
                    name={automation.id}
                    label={automation.name}
                    value={{
                        type: 'workflow',
                        id: automation.id,
                    }}
                    searchableText={automation.name}
                />
            ),
        ),
        [automations],
    );

    const handleChange = useCallback(
        (newValue: PicklistValue) => {
            const { name, value: newValueValue } = newValue;
            if (name === 'manychat-api') {
                return onChange({
                    type: 'manychat-api',
                });
            }
            if (name === 'conversations-api') {
                return onChange({
                    type: 'conversations-api',
                });
            }
            return onChange(newValueValue);
        },
        [onChange],
    );

    return (
        <Picklist
            disabled={disabled}
            isLoading={isLoading}
            value={picklistValue}
            onChange={handleChange}
        >
            <Option
                name="manychat-api"
                label="ManyChat API"
            />
            <Option
                name="conversations-api"
                label="Conversations API"
            />
            <Option variant="header" label="Workflows" />
            {workflowsList}
        </Picklist>
    );
};

export default ChannelDeliveryPicker;
